import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert, Row, Col, ButtonGroup, Image, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';
import { useAuth } from '../../contexts/AuthContext';

const TaskManager = ({ beacons, selectedBeacon, onRefresh, wsRef }) => {
  const [tasks, setTasks] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskFile, setTaskFile] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [createData, setCreateData] = useState({
    beaconId: '',
    command: 'shell',
    args: {}
  });
  const [filter, setFilter] = useState('all');
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [creatingActions, setCreatingActions] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadTasks();
  }, [selectedBeacon]);

  // Listen for WebSocket real-time task updates
  useEffect(() => {
    if (wsRef?.current) {
      const originalOnMessage = wsRef.current.onmessage;

      wsRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data);

        // Handle task updates
        if (message.type === 'task_completed' || message.type === 'task_created' || message.type === 'beacon_task_update') {
          console.log('[TaskManager] Real-time update:', message.type);
          // Reload tasks to get updated data
          loadTasks();
        }

        // Call original handler
        if (originalOnMessage) {
          originalOnMessage(event);
        }
      };
    }
  }, [wsRef]);

  const loadTasks = async () => {
    try {
      const filters = {};
      // TEMP: Disable beacon filter to show all tasks
      // if (selectedBeacon) {
      //   filters.beaconId = selectedBeacon.beaconId;
      // }
      const response = await c2Service.getTasks(filters);
      // Backend returns {success, count, data: [...tasks]}, axios wraps it
      const tasksData = response.data?.data || [];
      console.log('[TaskManager] Loaded tasks:', tasksData.length);

      // Debug: Check task data structure
      if (tasksData.length > 0) {
        console.log('[TaskManager] First task full object:', tasksData[0]);
        console.log('[TaskManager] First task _id:', tasksData[0]._id);
        console.log('[TaskManager] First task beacon:', tasksData[0].beacon);
      }

      // Debug: Check completed task data
      const completedTask = tasksData.find(t => t.status === 'completed');
      if (completedTask) {
        console.log('[TaskManager] Sample completed task:', {
          id: completedTask._id,
          command: completedTask.command,
          status: completedTask.status,
          createdAt: completedTask.createdAt,
          completedAt: completedTask.completedAt,
          result: completedTask.result,
          hasDuration: completedTask.result?.duration !== undefined
        });
      }

      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  };

  const handleCreateTask = async () => {
    try {
      const beacon = beacons.find(b => b.beaconId === createData.beaconId);
      if (!beacon) {
        alert('Please select a beacon');
        return;
      }

      await c2Service.createTask(beacon._id, createData.command, createData.args);
      setShowCreateModal(false);
      setCreateData({ beaconId: '', command: 'shell', args: {} });
      loadTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      alert('Failed to create task: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleApproveTask = async (taskId) => {
    if (!window.confirm('Approve this high-risk task?')) {
      return;
    }

    try {
      await c2Service.approveTask(taskId);
      loadTasks();
    } catch (error) {
      console.error('Failed to approve task:', error);
    }
  };

  const handleCancelTask = async (taskId) => {
    if (!window.confirm('Cancel this task?')) {
      return;
    }

    try {
      await c2Service.cancelTask(taskId);
      loadTasks();
    } catch (error) {
      console.error('Failed to cancel task:', error);
    }
  };

  const handleRetryTask = async (taskId) => {
    console.log('[TaskManager] Retry task called with ID:', taskId);
    console.log('[TaskManager] Task ID type:', typeof taskId);

    if (!window.confirm('Retry this task? It will be re-queued for execution.')) {
      return;
    }

    try {
      console.log('[TaskManager] Calling retryTask API with ID:', taskId);
      const response = await c2Service.retryTask(taskId);
      console.log('[TaskManager] Retry response:', response.data);
      loadTasks();
    } catch (error) {
      console.error('[TaskManager] Failed to retry task:', error);
      console.error('[TaskManager] Error response:', error.response?.data);
      alert('Failed to retry task: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleBulkCreateActions = async () => {
    const taskIds = Array.from(selectedTasks);

    if (taskIds.length === 0) {
      alert('Please select at least one task');
      return;
    }

    // Show preview of MITRE techniques
    const selectedTaskData = tasks.filter(t => taskIds.includes(t._id));
    const completedTasks = selectedTaskData.filter(t => t.status === 'completed' && !t.action);

    if (completedTasks.length === 0) {
      alert('No completed tasks without actions selected');
      return;
    }

    const techniquePreview = completedTasks
      .map(t => `${t.command}: ${t.mitreAttack?.techniques?.join(', ') || 'N/A'}`)
      .join('\n');

    if (!window.confirm(`Create ${completedTasks.length} red team actions?\n\nMITRE Techniques:\n${techniquePreview}`)) {
      return;
    }

    setCreatingActions(true);
    try {
      const response = await c2Service.bulkCreateActions(taskIds);
      const result = response.data.data;

      alert(`✅ Created ${result.created} actions\n⏭️ Skipped ${result.skipped} tasks\n❌ Failed ${result.failed} tasks`);

      // Clear selection and reload tasks
      setSelectedTasks(new Set());
      loadTasks();
    } catch (error) {
      console.error('Failed to bulk create actions:', error);
      alert('Failed to create actions: ' + (error.response?.data?.error || error.message));
    } finally {
      setCreatingActions(false);
    }
  };

  const handleSelectAll = () => {
    const completedTasksWithoutActions = filteredTasks
      .filter(t => t.status === 'completed' && !t.action)
      .map(t => t._id);
    setSelectedTasks(new Set(completedTasksWithoutActions));
  };

  const handleDeselectAll = () => {
    setSelectedTasks(new Set());
  };

  const handleToggleTask = (taskId) => {
    const newSelection = new Set(selectedTasks);
    if (newSelection.has(taskId)) {
      newSelection.delete(taskId);
    } else {
      newSelection.add(taskId);
    }
    setSelectedTasks(newSelection);
  };

  const openResultModal = async (task) => {
    // Use the task data we already have instead of fetching again
    setSelectedTask(task);
    setTaskFile(null);
    setShowResultModal(true);

    // If this is a screenshot task, fetch the associated file
    if (task.command === 'screenshot') {
      console.log('[TaskManager] Fetching screenshot file for task:', task._id);
      setLoadingFile(true);

      try {
        // First, get the list of files for this task
        const filesResponse = await c2Service.getFiles({});
        console.log('[TaskManager] Files response:', filesResponse.data);

        const taskFiles = filesResponse.data?.data || [];
        console.log('[TaskManager] Total files:', taskFiles.length);

        // Find file that matches this task
        const matchingFile = taskFiles.find(f => {
          console.log('[TaskManager] Checking file:', f._id, 'task:', f.task, 'vs', task._id);
          return f.task === task._id || f.task?._id === task._id;
        });

        if (matchingFile) {
          console.log('[TaskManager] Found matching file:', matchingFile._id);

          // Fetch the file content via preview endpoint
          const fileResponse = await c2Service.getFile(matchingFile._id);
          console.log('[TaskManager] File details response:', fileResponse.data);

          // Get preview with content
          const previewUrl = `/api/c2/files/${matchingFile._id}/preview`;
          const previewResponse = await fetch(previewUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const previewData = await previewResponse.json();
          console.log('[TaskManager] Preview response:', previewData);

          if (previewData.success && previewData.data?.content) {
            setTaskFile({
              ...matchingFile,
              content: previewData.data.content,
              mimeType: previewData.data.mimeType
            });
          }
        } else {
          console.log('[TaskManager] No matching file found for task:', task._id);
        }
      } catch (error) {
        console.error('[TaskManager] Failed to load screenshot file:', error);
      } finally {
        setLoadingFile(false);
      }
    }
  };

  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const filteredTasks = safeTasks.filter(t => {
    if (filter === 'pending') return t.status === 'pending' || t.status === 'sent';
    if (filter === 'completed') return t.status === 'completed';
    if (filter === 'approval') return !t.isApproved && t.requiresApproval;
    if (filter === 'no_action') return t.status === 'completed' && !t.action;
    return true;
  });

  const commandTemplates = {
    // Execution (core — always available)
    shell: { label: 'Shell Command', args: { cmd: '' }, category: 'Execution', platforms: ['windows', 'linux', 'darwin'] },
    powershell: { label: 'PowerShell', args: { cmd: '' }, category: 'Execution', platforms: ['windows'] },
    execute_assembly: { label: 'Execute Assembly', args: { assembly: '', args: [] }, category: 'Execution', platforms: ['windows'], module: 'modInjection' },

    // File Operations (core)
    ls: { label: 'List Directory', args: { path: '.' }, category: 'File Operations', platforms: ['windows', 'linux', 'darwin'] },
    cat: { label: 'Read File', args: { path: '' }, category: 'File Operations', platforms: ['windows', 'linux', 'darwin'] },
    download: { label: 'Download File', args: { remotePath: '' }, category: 'File Operations', platforms: ['windows', 'linux', 'darwin'] },
    upload: { label: 'Upload File', args: { remotePath: '', content: '' }, category: 'File Operations', platforms: ['windows', 'linux', 'darwin'] },
    rm: { label: 'Delete File', args: { path: '' }, category: 'File Operations', platforms: ['windows', 'linux', 'darwin'] },
    mkdir: { label: 'Create Directory', args: { path: '' }, category: 'File Operations', platforms: ['windows', 'linux', 'darwin'] },

    // Credentials (requires 'credentials' build tag)
    mimikatz: { label: 'Mimikatz', args: { module: 'sekurlsa::logonpasswords' }, category: 'Credentials', platforms: ['windows'], module: 'modCredentials' },
    lsass_dump: { label: 'LSASS Dump', args: {}, category: 'Credentials', platforms: ['windows'], module: 'modCredentials' },
    dcsync: { label: 'DCSync', args: { domain: '', user: '' }, category: 'Credentials', platforms: ['windows'], module: 'modCredentials' },
    kerberoast: { label: 'Kerberoast', args: {}, category: 'Credentials', platforms: ['windows'], module: 'modCredentials' },

    // Lateral Movement (requires 'lateral' build tag)
    psexec: { label: 'PSExec', args: { target: '', username: '', password: '', command: '' }, category: 'Lateral Movement', platforms: ['windows'], module: 'modLateral' },
    wmi_exec: { label: 'WMI Exec', args: { target: '', username: '', password: '', command: '' }, category: 'Lateral Movement', platforms: ['windows'], module: 'modLateral' },
    schtasks: { label: 'Scheduled Task', args: { target: '', taskName: 'WindowsUpdate', command: '' }, category: 'Lateral Movement', platforms: ['windows'], module: 'modLateral' },

    // Share Browsing (core — Windows only)
    enum_shares: { label: 'Enumerate Shares', args: { target: '', depth: 1 }, category: 'Enumeration', platforms: ['windows'] },
    browse_path: { label: 'Browse Path', args: { path: '' }, category: 'Enumeration', platforms: ['windows', 'linux', 'darwin'] },

    // Enumeration
    web_screenshot: { label: 'Web Screenshot', args: { target: '' }, category: 'Enumeration', platforms: ['windows', 'linux', 'darwin'] },
    screenshot: { label: 'Desktop Screenshot', args: {}, category: 'Enumeration', platforms: ['windows', 'linux', 'darwin'] },
    keylog_start: { label: 'Start Keylogger', args: {}, category: 'Enumeration', platforms: ['windows', 'linux', 'darwin'], module: 'modKeylogger' },
    keylog_stop: { label: 'Stop Keylogger', args: {}, category: 'Enumeration', platforms: ['windows', 'linux', 'darwin'], module: 'modKeylogger' },
    process_list: { label: 'Process List', args: {}, category: 'Enumeration', platforms: ['windows', 'linux', 'darwin'] },
    network_scan: { label: 'Network Scan', args: { target: '192.168.1.0/24' }, category: 'Enumeration', platforms: ['windows', 'linux', 'darwin'] },

    // Evasion (requires 'evasion' build tag)
    patch_amsi: { label: 'Patch AMSI', args: {}, category: 'Evasion', platforms: ['windows'], module: 'modEvasion' },
    patch_etw: { label: 'Patch ETW', args: {}, category: 'Evasion', platforms: ['windows'], module: 'modEvasion' },
    patch_both: { label: 'Patch AMSI & ETW', args: {}, category: 'Evasion', platforms: ['windows'], module: 'modEvasion' },

    // Control (core)
    sleep: { label: 'Change Sleep', args: { seconds: 60 }, category: 'Control', platforms: ['windows', 'linux', 'darwin'] },
    jitter: { label: 'Change Jitter', args: { jitter: 30 }, category: 'Control', platforms: ['windows', 'linux', 'darwin'] },
    kill: { label: 'Kill Beacon', args: {}, category: 'Control', platforms: ['windows', 'linux', 'darwin'] },
    exit: { label: 'Exit Beacon', args: {}, category: 'Control', platforms: ['windows', 'linux', 'darwin'] }
  };

  // Helper to check if command is available for selected beacon
  const isCommandAvailable = (cmd) => {
    if (!createData.beaconId) return true; // Show all if no beacon selected

    const beacon = beacons.find(b => b.beaconId === createData.beaconId);
    if (!beacon) return true;

    const template = commandTemplates[cmd];
    if (!template) return false;

    // Check platform compatibility
    if (beacon.systemInfo?.os) {
      const os = beacon.systemInfo.os.toLowerCase();
      if (template.platforms && !template.platforms.includes(os)) return false;
    }

    // Check module availability (build tag)
    if (template.module && beacon.build?.config) {
      return !!beacon.build.config[template.module];
    }

    // No module required, or no build info (legacy beacons — show all)
    return true;
  };

  const highRiskCommands = ['mimikatz', 'lsass_dump', 'dcsync', 'kerberoast', 'execute_assembly', 'psexec', 'wmi_exec', 'schtasks', 'rm', 'kill'];

  const getStatusBadge = (task) => {
    const statusConfig = {
      pending: { bg: 'secondary', text: 'Pending' },
      sent: { bg: 'info', text: 'Sent' },
      running: { bg: 'primary', text: 'Running' },
      completed: { bg: 'success', text: 'Completed' },
      failed: { bg: 'danger', text: 'Failed' },
      cancelled: { bg: 'dark', text: 'Cancelled' }
    };
    const config = statusConfig[task.status] || statusConfig.pending;
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const getMitreAttackBadges = (task) => {
    if (!task.mitreAttack?.techniques) return null;
    return task.mitreAttack.techniques.map(tech => (
      <Badge key={tech} bg="dark" className="me-1 mb-1">{tech}</Badge>
    ));
  };

  return (
    <>
      <Card>
        {/* FORCED Horizontal Header */}
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563', flexWrap: 'wrap', gap: '1rem' }}>
          <strong style={{ color: '#fff' }}>Tasks ({filteredTasks?.length || 0})</strong>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '0' }}>
              <button onClick={() => setFilter('all')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'all' ? '#3b82f6' : 'transparent', color: filter === 'all' ? '#fff' : '#3b82f6', border: '1px solid #3b82f6', borderRight: 'none', cursor: 'pointer', borderTopLeftRadius: '0.375rem', borderBottomLeftRadius: '0.375rem' }}>All</button>
              <button onClick={() => setFilter('pending')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'pending' ? '#06b6d4' : 'transparent', color: filter === 'pending' ? '#fff' : '#06b6d4', border: '1px solid #06b6d4', borderRight: 'none', cursor: 'pointer' }}>Pending</button>
              <button onClick={() => setFilter('completed')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'completed' ? '#10b981' : 'transparent', color: filter === 'completed' ? '#fff' : '#10b981', border: '1px solid #10b981', borderRight: 'none', cursor: 'pointer' }}>Completed</button>
              <button onClick={() => setFilter('approval')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'approval' ? '#f59e0b' : 'transparent', color: filter === 'approval' ? '#fff' : '#f59e0b', border: '1px solid #f59e0b', borderRight: 'none', cursor: 'pointer' }}>Approval</button>
              <button onClick={() => setFilter('no_action')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'no_action' ? '#8b5cf6' : 'transparent', color: filter === 'no_action' ? '#fff' : '#8b5cf6', border: '1px solid #8b5cf6', cursor: 'pointer', borderTopRightRadius: '0.375rem', borderBottomRightRadius: '0.375rem' }}>No Action</button>
            </div>
            {selectedTasks.size > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                  {selectedTasks.size} selected
                </span>
                <button
                  onClick={handleDeselectAll}
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#6b7280', color: '#fff', border: '1px solid #6b7280', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  Deselect All
                </button>
                <button
                  onClick={handleBulkCreateActions}
                  disabled={creatingActions}
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: creatingActions ? '#6b7280' : '#8b5cf6', color: '#fff', border: `1px solid ${creatingActions ? '#6b7280' : '#8b5cf6'}`, borderRadius: '0.375rem', cursor: creatingActions ? 'not-allowed' : 'pointer' }}
                >
                  {creatingActions ? 'Creating...' : 'Create Red Team Actions'}
                </button>
              </div>
            )}
            {selectedTasks.size === 0 && filter === 'no_action' && filteredTasks.length > 0 && (
              <button
                onClick={handleSelectAll}
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#8b5cf6', color: '#fff', border: '1px solid #8b5cf6', borderRadius: '0.375rem', cursor: 'pointer' }}
              >
                Select All ({filteredTasks.length})
              </button>
            )}
            <button
              onClick={loadTasks}
              style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#6b7280', color: '#fff', border: '1px solid #6b7280', borderRadius: '0.375rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
              title="Refresh task list"
            >
              <FontAwesomeIcon icon={faSync} />
              Refresh
            </button>
            {(user?.role === 'admin' || user?.permissions?.includes('c2.tasks.create')) && (
              <button
                onClick={() => {
                  setCreateData({ beaconId: selectedBeacon?.beaconId || '', command: 'shell', args: { cmd: '' } });
                  setShowCreateModal(true);
                }}
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#3b82f6', color: '#fff', border: '1px solid #3b82f6', borderRadius: '0.375rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Create Task
              </button>
            )}
          </div>
        </div>
        <div style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedTasks.size > 0 && filteredTasks.filter(t => t.status === 'completed' && !t.action).every(t => selectedTasks.has(t._id))}
                    onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Task ID</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Beacon</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Command</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>MITRE</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Action</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Created</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Completed</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Duration</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(task => (
                <tr key={task._id} style={{ borderBottom: '1px solid #374151' }}>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedTasks.has(task._id)}
                      onChange={() => handleToggleTask(task._id)}
                      disabled={task.status !== 'completed' || task.action}
                      style={{ cursor: (task.status !== 'completed' || task.action) ? 'not-allowed' : 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {getStatusBadge(task)}
                    {task.requiresApproval && !task.isApproved && (
                      <Badge bg="warning" className="ms-1">Approval Required</Badge>
                    )}
                  </td>
                  <td>
                    <code className="small text-muted" title={task._id} style={{ cursor: 'help' }}>
                      ...{task._id.slice(-4)}
                    </code>
                  </td>
                  <td>
                    <code className="small">{task.beacon?.beaconId || 'N/A'}</code>
                  </td>
                  <td>
                    <strong>{task.command}</strong>
                    {highRiskCommands.includes(task.command) && (
                      <Badge bg="danger" className="ms-1">HIGH RISK</Badge>
                    )}
                  </td>
                  <td>
                    {task.mitreAttack?.techniques?.[0] ? (
                      <Badge bg="dark">{task.mitreAttack.techniques[0]}</Badge>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>—</span>
                    )}
                  </td>
                  <td>
                    {task.action ? (
                      <Badge bg="success" title="Red team action created">
                        ✓ Created
                      </Badge>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>—</span>
                    )}
                  </td>
                  <td>
                    <small style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      {task.createdAt ? new Date(task.createdAt).toLocaleString() : '-'}
                    </small>
                  </td>
                  <td>
                    <small style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      {task.completedAt ? new Date(task.completedAt).toLocaleString() : '-'}
                    </small>
                  </td>
                  <td>
                    <small style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      {task.result?.duration ? `${task.result.duration}ms` : '-'}
                    </small>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {task.status === 'completed' && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openResultModal(task)}
                          style={{ minWidth: '100px' }}
                        >
                          View Result
                        </Button>
                      )}
                      {task.requiresApproval && !task.isApproved && (user?.role === 'admin' || user?.permissions?.includes('c2.tasks.approve')) && (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleApproveTask(task._id)}
                          style={{ minWidth: '80px' }}
                        >
                          Approve
                        </Button>
                      )}
                      {(task.status === 'pending' || task.status === 'sent') && (user?.role === 'admin' || user?.permissions?.includes('c2.tasks.cancel')) && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleCancelTask(task._id)}
                          style={{ minWidth: '70px' }}
                        >
                          Cancel
                        </Button>
                      )}
                      {(task.status === 'completed' || task.status === 'failed' || task.status === 'sent') && (user?.role === 'admin' || user?.permissions?.includes('c2.tasks.create')) && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleRetryTask(task._id)}
                          title="Re-queue this task for execution"
                          style={{ minWidth: '60px' }}
                        >
                          Retry
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredTasks.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem 0' }}>
              <p style={{ marginBottom: '0.5rem' }}>No tasks found</p>
              <small>Create a task to get started</small>
            </div>
          )}
        </div>
      </Card>

      {/* Create Task Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Beacon</Form.Label>
              <Form.Select
                value={createData.beaconId}
                onChange={(e) => setCreateData({ ...createData, beaconId: e.target.value })}
              >
                <option value="">Select beacon...</option>
                {beacons.filter(b => b.isApproved && b.isAlive).map(beacon => (
                  <option key={beacon._id} value={beacon.beaconId}>
                    {beacon.beaconId} - {beacon.systemInfo?.hostname}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Command</Form.Label>
              <Form.Select
                value={createData.command}
                onChange={(e) => {
                  const template = commandTemplates[e.target.value];
                  setCreateData({
                    ...createData,
                    command: e.target.value,
                    args: template ? { ...template.args } : {}
                  });
                }}
              >
                <option value="">Select command...</option>
                {Object.entries(commandTemplates).map(([cmd, config]) => {
                  const available = isCommandAvailable(cmd);
                  const beacon = beacons.find(b => b.beaconId === createData.beaconId);
                  let disabledReason = '';
                  if (!available && beacon) {
                    if (config.module && beacon.build?.config && !beacon.build.config[config.module]) {
                      disabledReason = '(module not compiled)';
                    } else if (config.platforms && beacon.systemInfo?.os) {
                      disabledReason = '(not available on this platform)';
                    }
                  }
                  return (
                    <option
                      key={cmd}
                      value={cmd}
                      disabled={!available}
                      style={{ color: available ? 'inherit' : '#666' }}
                    >
                      {config.label} {disabledReason}
                    </option>
                  );
                })}
              </Form.Select>
              {highRiskCommands.includes(createData.command) && (
                <Alert variant="danger" className="mt-2 mb-0">
                  <strong>⚠️ High-Risk Command</strong>
                  <p className="mb-0 mt-1">
                    This command requires approval from c2_lead. The task will be created in "pending_approval" status.
                  </p>
                </Alert>
              )}
            </Form.Group>

            {/* Command-specific arguments */}
            {/* EXECUTION */}
            {(createData.command === 'shell' || createData.command === 'powershell') && (
              <Form.Group className="mb-3">
                <Form.Label>{createData.command === 'powershell' ? 'PowerShell Command' : 'Shell Command'} *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={createData.command === 'powershell' ? 'Get-Process | Select-Object -First 10' : 'whoami'}
                  value={createData.args.cmd || ''}
                  onChange={(e) => setCreateData({ ...createData, args: { cmd: e.target.value } })}
                  required
                />
              </Form.Group>
            )}

            {createData.command === 'execute_assembly' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Assembly (Base64) *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Base64-encoded .NET assembly"
                    value={createData.args.assembly || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, assembly: e.target.value } })}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Arguments (optional, comma-separated)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="arg1,arg2,arg3"
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, args: e.target.value.split(',').filter(a => a.trim()) } })}
                  />
                </Form.Group>
              </>
            )}

            {/* FILE OPERATIONS */}
            {(createData.command === 'ls' || createData.command === 'cat' || createData.command === 'rm' || createData.command === 'mkdir') && (
              <Form.Group className="mb-3">
                <Form.Label>Path {createData.command !== 'ls' && '*'}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={createData.command === 'ls' ? 'C:\\Windows\\System32 (default: current dir)' :
                               createData.command === 'cat' ? 'C:\\Windows\\System32\\drivers\\etc\\hosts' :
                               createData.command === 'rm' ? 'C:\\Temp\\file.txt' :
                               'C:\\Temp\\newfolder'}
                  value={createData.args.path || ''}
                  onChange={(e) => setCreateData({ ...createData, args: { path: e.target.value } })}
                  required={createData.command !== 'ls'}
                />
              </Form.Group>
            )}

            {createData.command === 'download' && (
              <Form.Group className="mb-3">
                <Form.Label>Remote Path *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="C:\\Users\\admin\\Desktop\\passwords.txt"
                  value={createData.args.remotePath || ''}
                  onChange={(e) => setCreateData({ ...createData, args: { remotePath: e.target.value } })}
                  required
                />
                <Form.Text className="text-muted">Max file size: 50MB</Form.Text>
              </Form.Group>
            )}

            {createData.command === 'upload' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>File to Upload *</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const base64 = event.target.result.split(',')[1];
                          setCreateData({
                            ...createData,
                            args: { ...createData.args, content: base64 }
                          });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    required
                  />
                  <Form.Text className="text-muted">Max file size: 10MB</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Remote Path *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="C:\\Windows\\Temp\\payload.exe"
                    value={createData.args.remotePath || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, remotePath: e.target.value } })}
                    required
                  />
                </Form.Group>
              </>
            )}

            {/* CREDENTIALS */}
            {createData.command === 'mimikatz' && (
              <Form.Group className="mb-3">
                <Form.Label>Mimikatz Module</Form.Label>
                <Form.Select
                  value={createData.args.module || 'sekurlsa::logonpasswords'}
                  onChange={(e) => setCreateData({ ...createData, args: { module: e.target.value } })}
                >
                  <option value="sekurlsa::logonpasswords">sekurlsa::logonpasswords</option>
                  <option value="sekurlsa::tickets">sekurlsa::tickets</option>
                  <option value="lsadump::sam">lsadump::sam</option>
                  <option value="lsadump::secrets">lsadump::secrets</option>
                  <option value="kerberos::list">kerberos::list</option>
                  <option value="kerberos::ptt">kerberos::ptt</option>
                </Form.Select>
              </Form.Group>
            )}

            {createData.command === 'dcsync' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Domain (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="CONTOSO.LOCAL (leave empty for current domain)"
                    value={createData.args.domain || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, domain: e.target.value } })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>User *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Administrator or krbtgt"
                    value={createData.args.user || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, user: e.target.value } })}
                    required
                  />
                </Form.Group>
              </>
            )}

            {/* LATERAL MOVEMENT */}
            {(createData.command === 'psexec' || createData.command === 'wmi_exec') && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Target IP/Hostname *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="192.168.1.100 or SERVER01"
                    value={createData.args.target || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, target: e.target.value } })}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Username (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="DOMAIN\\Administrator"
                    value={createData.args.username || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, username: e.target.value } })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Password (optional)</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="P@ssw0rd"
                    value={createData.args.password || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, password: e.target.value } })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Command (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="whoami"
                    value={createData.args.command || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, command: e.target.value } })}
                  />
                </Form.Group>
              </>
            )}

            {createData.command === 'schtasks' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Target IP/Hostname *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="192.168.1.100"
                    value={createData.args.target || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, target: e.target.value } })}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Task Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="WindowsUpdate"
                    value={createData.args.taskName || 'WindowsUpdate'}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, taskName: e.target.value } })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Command (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="C:\\Windows\\System32\\cmd.exe /c whoami"
                    value={createData.args.command || ''}
                    onChange={(e) => setCreateData({ ...createData, args: { ...createData.args, command: e.target.value } })}
                  />
                </Form.Group>
              </>
            )}

            {/* ENUMERATION */}
            {createData.command === 'network_scan' && (
              <Form.Group className="mb-3">
                <Form.Label>Target (CIDR or IP)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="192.168.1.0/24 or 192.168.1.100"
                  value={createData.args.target || '192.168.1.0/24'}
                  onChange={(e) => setCreateData({ ...createData, args: { target: e.target.value } })}
                />
                <Form.Text className="text-muted">Max /24 network (254 hosts)</Form.Text>
              </Form.Group>
            )}

            {/* CONTROL */}
            {createData.command === 'sleep' && (
              <Form.Group className="mb-3">
                <Form.Label>Sleep Interval (seconds) *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="3600"
                  value={createData.args.seconds || 60}
                  onChange={(e) => setCreateData({ ...createData, args: { seconds: parseInt(e.target.value) } })}
                  required
                />
                <Form.Text className="text-muted">1-3600 seconds (1 hour max)</Form.Text>
              </Form.Group>
            )}

            {createData.command === 'jitter' && (
              <Form.Group className="mb-3">
                <Form.Label>Jitter Percentage *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={createData.args.jitter || 30}
                  onChange={(e) => setCreateData({ ...createData, args: { jitter: parseInt(e.target.value) } })}
                  required
                />
                <Form.Text className="text-muted">0-100%</Form.Text>
              </Form.Group>
            )}

            {/* Commands with no arguments */}
            {['screenshot', 'lsass_dump', 'kerberoast', 'keylog_start', 'keylog_stop', 'process_list', 'patch_amsi', 'patch_etw', 'patch_both', 'kill', 'exit'].includes(createData.command) && (
              <Alert variant="info">
                <small>This command requires no additional arguments.</small>
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateTask}>
            Create Task
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Task Result Modal */}
      <Modal show={showResultModal} onHide={() => setShowResultModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Task Result</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Beacon:</strong> {selectedTask.beacon?.beaconId}
                </Col>
                <Col md={6}>
                  <strong>Command:</strong> {selectedTask.command}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Status:</strong> {getStatusBadge(selectedTask)}
                </Col>
                <Col md={6}>
                  <strong>Duration:</strong> {selectedTask.result?.duration || 0}ms
                </Col>
              </Row>

              {selectedTask.result?.error && (
                <Alert variant="danger">
                  <strong>Error:</strong>
                  <pre className="mb-0 mt-2">{selectedTask.result.error}</pre>
                </Alert>
              )}

              {selectedTask.command === 'screenshot' && (
                <div className="mt-3">
                  <strong>Screenshot:</strong>
                  {loadingFile && (
                    <div className="text-center my-3">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted">Loading screenshot...</p>
                    </div>
                  )}
                  {!loadingFile && taskFile && taskFile.content && (
                    <div className="text-center mt-2">
                      <Image
                        src={`data:${taskFile.mimeType || 'image/png'};base64,${taskFile.content}`}
                        fluid
                        style={{ maxHeight: '500px', border: '1px solid #374151', borderRadius: '4px' }}
                        alt="Screenshot"
                      />
                      <div className="mt-2 text-muted">
                        <small>
                          {taskFile.metadata?.filename} ({Math.round((taskFile.metadata?.size || 0) / 1024)} KB)
                        </small>
                      </div>
                    </div>
                  )}
                  {!loadingFile && !taskFile && (
                    <Alert variant="warning" className="mt-2">
                      No screenshot file found for this task.
                    </Alert>
                  )}
                </div>
              )}

              {selectedTask.result?.output && selectedTask.command !== 'screenshot' && (
                <>
                  <strong>Output:</strong>
                  <pre className="bg-dark text-light p-3 mt-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                    {selectedTask.result.output}
                  </pre>
                </>
              )}

              {selectedTask.mitreAttack && (
                <div className="mt-3">
                  <strong>MITRE ATT&CK:</strong>
                  <div className="mt-2">
                    {selectedTask.mitreAttack.tactics?.map(tactic => (
                      <Badge key={tactic} bg="info" className="me-1 mb-1">{tactic}</Badge>
                    ))}
                    {selectedTask.mitreAttack.techniques?.map(tech => (
                      <Badge key={tech} bg="dark" className="me-1 mb-1">{tech}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResultModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskManager;
